import axiosConfig from './axiosConfig';
import { useMutation, useQuery } from '@tanstack/react-query';
import { exportData } from './export';
import { createListFetcher } from './listFetchers';
import { queryClient } from './queryClient';

// Create work order
export const createWorkOrder = async (workOrderData) => {
    try {
        const response = await axiosConfig.post('workorder', workOrderData, { headers: { 'Content-Type': 'multipart/form-data' } });
        return response;
    } catch (error) {
        throw error;
    }
};

// Show work order details
export const getWorkOrder = async (id) => {
    try {
        const response = await axiosConfig.get(`workorder/${id}`);
        return response;
    } catch (error) {
        throw error;
    }
};

// Update work order
export const updateWorkOrder = async (id, workOrderData) => {
    try {
        const response = await axiosConfig.put(`workorder/${id}`, workOrderData, { headers: { 'Content-Type': 'multipart/form-data' } });
        return response;
    } catch (error) {
        throw error;
    }
};

// Export work orders (CSV)
export const exportWorkOrdersCSV = async (queryParams = {}) => {
    return exportData('workorders/export', 'workOrders', queryParams);
};

// Export work orders (XLSX)
export const exportWorkOrdersXLSX = async (queryParams = {}) => {
    return exportData('workorders/export/xlsx', 'workOrders', queryParams);
};

// Calendar data
export const getWorkOrdersCalendar = async (queryParams = {}) => {
    try {
        const response = await axiosConfig.get('workorders/calendar', { params: queryParams });
        return response;
    } catch (error) {
        throw error;
    }
};

export const useWorkOrdersCalendar = (queryParams = {}, options = {}) => useQuery({
    queryKey: ['workOrdersCalendar', queryParams],
    queryFn: () => getWorkOrdersCalendar(queryParams).then(response => response.data ?? []),
    keepPreviousData: true,
    retry: 1,
    ...options,
});

// Summary metrics
export const getWorkOrdersSummary = async (queryParams = {}) => {
    try {
        const response = await axiosConfig.get('workorders/summary', { params: queryParams });
        return response;
    } catch (error) {
        throw error;
    }
};

export const useWorkOrdersSummary = (queryParams = {}, options = {}) => useQuery({
    queryKey: ['workOrdersSummary', queryParams],
    queryFn: () => getWorkOrdersSummary(queryParams).then(response => response.data),
    keepPreviousData: true,
    retry: 1,
    ...options,
});


export const fetchWorkOrders = createListFetcher('workorder');

export const useCreateWorkOrder = (options = {}) => {
    return useMutation({
        mutationFn: createWorkOrder,
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['workOrders'] }),
        ...options,
    });
};

export const useUpdateWorkOrder = (options = {}) => {
    return useMutation({
        mutationFn: ({ id, workOrderData }) => updateWorkOrder(id, workOrderData),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['workOrders'] }),
        ...options,
    });
};

export const useDuplicateWorkOrder = (options = {}) => {
    return useMutation({
        mutationFn: ({ id, data }) => duplicateWorkOrder(id, data),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['workOrders'] }),
        ...options,
    });
};

// Trends analytics
export const getWorkOrdersTrends = async (queryParams = {}) => {
    try {
        const response = await axiosConfig.get('workorders/trends', { params: queryParams });
        return response;
    } catch (error) {
        throw error;
    }
};

export const getWorkOrderCategories = async () => {
    try {
        const response = await axiosConfig.get('workorders/categories');
        return response;
    } catch (error) {
        throw error;
    }
};

export const getWorkOrderActions = async (id, queryParams = {}) => {
    try {
        const response = await axiosConfig.get(`workorders/${id}/actions`, { params: queryParams });
        return response;
    } catch (error) {
        throw error;
    }
};

// Duplicate work order
export const duplicateWorkOrder = async (id, data) => {
    try {
        const response = await axiosConfig.post(`workorders/${id}/duplicate`, data);
        return response;
    } catch (error) {
        throw error;
    }
};